import { call, put, takeLatest } from 'redux-saga/effects';
import axios from 'axios';
import {
  LIST_REQUEST, listSourcesSuccess, listSourcesFailure,
  CREATE_REQUEST, createSourceSuccess, createSourceFailure,
  UPDATE_REQUEST, updateSourceSuccess, updateSourceFailure,
  DELETE_REQUEST, deleteSourceSuccess, deleteSourceFailure,
  TEST_REQUEST, testConnectionSuccess, testConnectionFailure,
  SCHEMA_REQUEST, getSchemaSuccess, getSchemaFailure,
  GET_REQUEST, getSourceSuccess, getSourceFailure
} from '../../actions/ingestion/sources';
import config from '../../../config';
import { getLocalUser } from '../../../utils/localUser';
import { Error as ErrorAction } from '../../actions/error/error';
import { 
  processApiResponse, 
  handleSagaError, 
  createSuccessPayload, 
  createErrorPayload 
} from '../../../utils/apiResponseHandler';

// Helper function to get headers with workspace ID
function getHeaders() {
  const userHeaders = getLocalUser();
  // getLocalUser() already includes X-WORKSPACE-ID from localStorage
  return userHeaders;
}

// List sources saga
function* listSourcesSaga(action) {
  try {
    const headers = getHeaders();
    
    const response = yield call(
      axios.get,
      `${config.API_URL}/ingestion/sources`,
      { headers }
    );
    
    // Use unified response processing
    const result = processApiResponse(response, 'list data sources');
    
    if (result.success) {
      yield put(listSourcesSuccess(result.data));
    } else {
      throw new Error(result.error);
    }
  } catch (error) {
    const errorPayload = createErrorPayload(error, 'list data sources');
    yield put(ErrorAction(error));
    yield put(listSourcesFailure(errorPayload));
  }
}

// Create source saga
function* createSourceSaga(action) {
  try {
    const headers = getHeaders();
    
    const response = yield call(
      axios.post,
      `${config.API_URL}/ingestion/sources`,
      action.payload,
      { headers }
    );
    
    // Use unified response processing
    const result = processApiResponse(response, 'create data source');
    
    if (result.success) {
      yield put(createSourceSuccess(createSuccessPayload(result.data)));
      yield put({ 
        type: 'SHOW_SUCCESS_SNACKBAR', 
        message: result.message
      });
    } else {
      throw new Error(result.error);
    }
  } catch (error) {
    const errorPayload = createErrorPayload(error, 'create data source');
    yield put(ErrorAction(error));
    yield put(createSourceFailure(errorPayload));
    yield put({ type: 'SHOW_ERROR_SNACKBAR', message: errorPayload.message });
  }
}

// Update source saga
function* updateSourceSaga(action) {
  try {
    const headers = getHeaders();
    const { sourceId, ...updateData } = action.payload;
    
    const response = yield call(
      axios.put,
      `${config.API_URL}/ingestion/sources/${sourceId}`,
      updateData,
      { headers }
    );
    
    // Use unified response processing
    const result = processApiResponse(response, 'update data source');
    
    if (result.success) {
      yield put(updateSourceSuccess(createSuccessPayload({ ...result.data, source_id: sourceId })));
      yield put({ type: 'SHOW_SUCCESS_SNACKBAR', message: result.message });
    } else {
      throw new Error(result.error);
    }
  } catch (error) {
    const errorPayload = createErrorPayload(error, 'update data source');
    yield put(ErrorAction(error));
    yield put(updateSourceFailure(errorPayload));
    yield put({ type: 'SHOW_ERROR_SNACKBAR', message: errorPayload.message });
  }
}

// Delete source saga
function* deleteSourceSaga(action) {
  try {
    const headers = getHeaders();
    const { sourceId } = action.payload;
    
    const response = yield call(
      axios.delete,
      `${config.API_URL}/ingestion/sources/${sourceId}`,
      { headers }
    );
    
    // Use unified response processing
    const result = processApiResponse(response, 'delete data source');
    
    if (result.success) {
      yield put(deleteSourceSuccess(createSuccessPayload({ sourceId })));
      yield put({ type: 'SHOW_SUCCESS_SNACKBAR', message: result.message });
    } else {
      throw new Error(result.error);
    }
  } catch (error) {
    const errorPayload = createErrorPayload(error, 'delete data source');
    yield put(ErrorAction(error));
    yield put(deleteSourceFailure(errorPayload));
    yield put({ type: 'SHOW_ERROR_SNACKBAR', message: errorPayload.message });
  }
}

// Test connection saga
function* testConnectionSaga(action) {
  try {
    const headers = getHeaders();
    const { sourceId, testData } = action.payload;
    
    // New test-connection endpoint that doesn't require existing source
    if (testData) {
      const response = yield call(
        axios.post,
        `${config.API_URL}/ingestion/sources/test-connection`,
        testData,
        { 
          headers,
          validateStatus: (status) => status < 500 // Accept 2xx, 3xx, and 4xx as valid responses
        }
      );
      
      const responseData = response.data?.data || response.data;
      
      if (responseData.status === 'success') {
        yield put(testConnectionSuccess({ 
          sourceId, 
          ...responseData,
          details: responseData.details || {}
        }));
      } else if (responseData.status === 'failed') {
        // Handle test failure with enhanced error details
        const errorDetails = responseData.error_details || {};
        const errorMessage = errorDetails.message || responseData.error || 'Connection test failed';
        const suggestion = errorDetails.suggestion || '';
        const category = errorDetails.category || 'unknown';
        
        throw {
          message: errorMessage,
          suggestion,
          category,
          details: errorDetails,
          error: responseData.error,
          connector_type: responseData.connector_type,
          status: responseData.status
        };
      } else {
        // Handle unexpected response
        throw new Error('Unexpected response from test connection');
      }
    } else {
      // Legacy test for existing sources
      const response = yield call(
        axios.post,
        `${config.API_URL}/ingestion/sources/${sourceId}/test`,
        {},
        { headers }
      );
      
      const result = processApiResponse(response, 'test connection');
      
      if (result.success) {
        yield put(testConnectionSuccess({ sourceId, ...result.data }));
        yield put({ type: 'SHOW_SUCCESS_SNACKBAR', message: result.message });
      } else {
        throw new Error(result.error);
      }
    }
  } catch (error) {
    let errorPayload;
    
    // Check if error has our enhanced structure (thrown from above)
    if (error.category) {
      errorPayload = {
        message: error.message,
        suggestion: error.suggestion,
        category: error.category,
        details: error.details,
        error: error.error,
        connector_type: error.connector_type,
        status: error.status,
        timestamp: new Date().toISOString()
      };
    } else if (error.response?.data) {
      // Handle axios errors (400, 500, etc.)
      const responseData = error.response.data.data || error.response.data;
      
      if (responseData.status === 'failed' && responseData.error_details) {
        // Extract detailed error information from the response
        errorPayload = {
          message: responseData.error_details.message || responseData.error || 'Connection test failed',
          suggestion: responseData.error_details.suggestion || '',
          category: responseData.error_details.category || 'unknown',
          details: responseData.error_details,
          error: responseData.error, // Keep the original error message
          connector_type: responseData.connector_type,
          status: responseData.status,
          timestamp: new Date().toISOString()
        };
      } else {
        // Fallback to default error handling
        errorPayload = createErrorPayload(error, 'test connection');
        
        // Try to extract any available error details
        if (responseData.error_details) {
          errorPayload.suggestion = responseData.error_details.suggestion;
          errorPayload.category = responseData.error_details.category;
          errorPayload.details = responseData.error_details;
          errorPayload.message = responseData.error_details.message || errorPayload.message;
        }
      }
    } else {
      // Handle other types of errors (network, etc.)
      errorPayload = createErrorPayload(error, 'test connection');
    }
    
    yield put(ErrorAction(error));
    yield put(testConnectionFailure({ 
      sourceId: action.payload.sourceId, 
      ...errorPayload
    }));
    
    // Only show snackbar for existing sources
    if (!action.payload.testData) {
      yield put({ type: 'SHOW_ERROR_SNACKBAR', message: errorPayload.message });
    }
  }
}

// Get schema saga
function* getSchemaSaga(action) {
  try {
    const headers = getHeaders();
    const { sourceId } = action.payload;
    
    const response = yield call(
      axios.get,
      `${config.API_URL}/ingestion/sources/${sourceId}/schema`,
      { headers }
    );
    
    // Use unified response processing
    const result = processApiResponse(response, 'get schema');
    
    if (result.success) {
      yield put(getSchemaSuccess({ sourceId, schema: result.data.schema || result.data }));
    } else {
      throw new Error(result.error);
    }
  } catch (error) {
    const errorPayload = createErrorPayload(error, 'get schema');
    yield put(ErrorAction(error));
    yield put(getSchemaFailure(errorPayload));
    yield put({ type: 'SHOW_ERROR_SNACKBAR', message: errorPayload.message });
  }
}

// Get single source saga
function* getSourceSaga(action) {
  try {
    const headers = getHeaders();
    const { sourceId } = action.payload;
    
    const response = yield call(
      axios.get,
      `${config.API_URL}/ingestion/sources/${sourceId}`,
      { headers }
    );
    
    // Use unified response processing
    const result = processApiResponse(response, 'get source');
    
    if (result.success) {
      yield put(getSourceSuccess(result.data));
    } else {
      throw new Error(result.error);
    }
  } catch (error) {
    const errorPayload = createErrorPayload(error, 'get source');
    yield put(ErrorAction(error));
    yield put(getSourceFailure(errorPayload));
    yield put({ type: 'SHOW_ERROR_SNACKBAR', message: errorPayload.message });
  }
}

// Watcher saga
export function* watch() {
  yield takeLatest(LIST_REQUEST, listSourcesSaga);
  yield takeLatest(CREATE_REQUEST, createSourceSaga);
  yield takeLatest(UPDATE_REQUEST, updateSourceSaga);
  yield takeLatest(DELETE_REQUEST, deleteSourceSaga);
  yield takeLatest(TEST_REQUEST, testConnectionSaga);
  yield takeLatest(SCHEMA_REQUEST, getSchemaSaga);
  yield takeLatest(GET_REQUEST, getSourceSaga);
}
